import cn from 'classnames';

import { useDictionary } from 'src/hooks/useDictionary';
import { useEnvVariables } from 'src/hooks/useEnvVariables';
import { usePdfHelpers } from 'src/hooks/usePdfHelpers';
import { useRouteContext } from 'src/hooks/useRouteContext';

import { formatEmail } from 'src/utils/formatEmail';
import { formatName } from 'src/utils/formatName';
import { formatPhone } from 'src/utils/formatPhone';

import LinkedInIcon from 'assets/icons/linkedin.svg';
import PdfIcon from 'assets/icons/pdf.svg';
import VcardIcon from 'assets/icons/vcard.svg';

import Breadcrumbs from 'components/BaseHelpers/Breadcrumbs';
import Image from 'components/BaseHelpers/Image';
import Link from 'components/BaseHelpers/Link';
import PatternBackground from 'components/BaseHelpers/PatternBackground';
import Text from 'components/BaseHelpers/Text';

import { ComponentProps } from '../../../types/component-props';
import { CorePeopleHeroData } from '../../../types/components';

import styles from './CorePeopleHero.module.scss';

export type CorePeopleHeroProps = ComponentProps & CorePeopleHeroData;

const CorePeopleHero = (props: CorePeopleHeroProps): JSX.Element => {
  const {
    image,
    backgroundImage,
    firstName,
    middleName,
    lastName,
    email,
    phone,
    mobile,
    fax,
    title,
    offices,
    linkedIn,
    breadcrumbs,
  } = props.fields;

  const {
    officeLabel,
    emailLabel,
    phoneLabel,
    businessPhoneLabel,
    mobilePhoneLabel,
    faxLabel,
    downloadLabel,
  } = useDictionary();

  const fullName = formatName({ firstName, lastName, middleName });
  const emailFormatted = formatEmail(email);
  const phoneFormatted = formatPhone(phone);
  const mobileFormatted = formatPhone(mobile);
  const faxFormatted = formatPhone(fax);

  const routeContext = useRouteContext();
  const resize = routeContext.routeData?.envVars.resizeImages;

  const getCanonicalUrl = () => {
    const metaDataComponent = routeContext.routeData?.route.placeholders.head?.find(
      (component) => component.componentName === 'CoreMetadata'
    );
    const canonicalUrl = metaDataComponent?.fields.canonicalUrl || '';
    return canonicalUrl;
  };

  const canonicalUrl = getCanonicalUrl();
  const { pdfPrintServerUrl } = useEnvVariables();
  const { isPdf } = usePdfHelpers();

  return (
    <div className={styles.main}>
      <>
        {backgroundImage ? (
          <Image
            {...backgroundImage}
            className={cn(styles['background-image'], {
              [styles['pdf-background-image']]: isPdf,
              [styles['pdf-background-image--full-width']]: !image,
            })}
            unoptimized={isPdf ? true : resize !== 'true'}
            alt=""
            fill
            fetchPriority="high"
            data-component="hero-image"
          />
        ) : (
          <PatternBackground
            className={cn(styles['fallback-background'], {
              [styles['pdf-background-image']]: isPdf,
              [styles['pdf-background-image--full-width']]: !image,
            })}
            fetchPriority="high"
            size="medium"
            theme="dark"
          />
        )}
      </>
      <div className={cn(styles.container, 'container-10')}>
        <Breadcrumbs breadcrumbs={breadcrumbs} className={styles.breadcrumbs} />
        <div className={styles['grid']}>
          {image && (
            <div className={styles['image-container']}>
              <Image {...image} className={styles['profile-image']} fill />
            </div>
          )}
          <div className={styles['content']}>
            <Text tag="h1" className={styles['name']} field={fullName} />
            <Text tag="p" className={cn(styles['title'], 'body-sm')} field={title} />
            <div className={styles['info']}>
              {offices.length > 0 && (
                <div className={styles['office-info']}>
                  <span className={cn(styles['info-label'], 'body-eyebrow')}>{officeLabel}</span>
                  <ul className={styles.offices}>
                    {offices.map((office, index) => (
                      <li key={index} className={styles['office-item']}>
                        <Link
                          {...office}
                          className={cn(
                            {
                              [styles['pdf-link']]: isPdf,
                            },
                            styles['office-link']
                          )}
                        >
                          {office.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className={styles['contact-info-section']}>
                {emailFormatted && (
                  <div className={styles['contact-info']}>
                    <span className={cn(styles['info-label'], 'body-eyebrow')}>{emailLabel}</span>
                    <Link
                      href={emailFormatted.href}
                      className={cn(
                        {
                          [styles['pdf-link']]: isPdf,
                        },
                        styles['email-link']
                      )}
                    >
                      <span
                        className={styles['email-formatted']}
                        dangerouslySetInnerHTML={{ __html: emailFormatted.emailHtml }}
                      />
                    </Link>
                  </div>
                )}
                {Boolean(phone || mobile) && (
                  <div className={cn(styles['contact-info'], styles['phone-info'])}>
                    <span className={cn(styles['info-label'], 'body-eyebrow')}>{phoneLabel}</span>
                    <ul className={styles['phone-numbers']}>
                      {phoneFormatted && (
                        <li className={styles['phone-item']}>
                          {Boolean(phoneFormatted && mobileFormatted) && (
                            <span className={cn(styles['info-label'], 'body-eyebrow')}>
                              {businessPhoneLabel}
                            </span>
                          )}
                          <Link
                            className={cn(
                              {
                                [styles['pdf-link']]: isPdf,
                              },
                              styles['phone-link']
                            )}
                            href={phoneFormatted?.href}
                          >
                            <span
                              className={styles['phone-formatted']}
                              dangerouslySetInnerHTML={{ __html: phoneFormatted?.text }}
                            />
                          </Link>
                        </li>
                      )}
                      {mobileFormatted && (
                        <li className={styles['phone-item']}>
                          {Boolean(phoneFormatted && mobileFormatted) && (
                            <span className={cn(styles['info-label'], 'body-eyebrow')}>
                              {mobilePhoneLabel}
                            </span>
                          )}
                          <Link
                            className={cn(
                              {
                                [styles['pdf-link']]: isPdf,
                              },
                              styles['phone-link']
                            )}
                            href={mobileFormatted?.href}
                          >
                            <span
                              className={styles['phone-formatted']}
                              dangerouslySetInnerHTML={{ __html: mobileFormatted?.text }}
                            />
                          </Link>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
                {faxFormatted && (
                  <div className={styles['contact-info']}>
                    <span className={cn(styles['info-label'], 'body-eyebrow')}>{faxLabel}</span>
                    <Link
                      href={faxFormatted.href}
                      className={cn(
                        {
                          [styles['pdf-link']]: isPdf,
                        },
                        styles['fax-link']
                      )}
                    >
                      <span
                        className={styles['fax-formatted']}
                        dangerouslySetInnerHTML={{ __html: faxFormatted.text }}
                      />
                    </Link>
                  </div>
                )}
              </div>
              <div className={styles['share-section']}>
                {linkedIn && (
                  <span className={styles['linked-in-wrapper']}>
                    <a
                      href={linkedIn}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles['linkedin-link']}
                      aria-label="LinkedIn Profile"
                    >
                      <LinkedInIcon className={styles['linkedin-icon']} />
                    </a>
                  </span>
                )}
                <div className={styles['files']}>
                  <span className={cn(styles['info-label'], 'body-eyebrow')}>{downloadLabel}</span>
                  <ul className={styles['file-links']}>
                    <li className={styles['file-item']}>
                      <a
                        href={getVcardUrl(canonicalUrl)}
                        className={styles['file-link']}
                        aria-label="Download vCard"
                        download={true}
                      >
                        <VcardIcon className={styles['file-icon']} />
                      </a>
                    </li>
                    <li className={styles['file-item']}>
                      <a
                        href={getPdfUrl(canonicalUrl, pdfPrintServerUrl)}
                        className={styles['file-link']}
                        aria-label="Download PDF"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <PdfIcon className={styles['file-icon']} />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const getPdfUrl = (canonicalUrl: string, pdfBaseUrl: string) => {
  const url = new URL(canonicalUrl || 'https://www.dwlaw.com/');
  const filename = url.pathname.split('/').filter(Boolean).pop() || 'professional';
  url.pathname = '/pdf' + url.pathname;

  const pdfPageUrl = url.toString();

  const pdfServerUrl = pdfBaseUrl?.endsWith('/') ? pdfBaseUrl : pdfBaseUrl + '/';

  return `${pdfServerUrl}${filename}.pdf/?url=${pdfPageUrl}`;
};

const getVcardUrl = (canonicalUrl: string) => {
  const url = new URL(canonicalUrl || 'https://www.dwlaw.com/');
  const slug = url.pathname.split('/').filter(Boolean).pop();
  return `/api/vcard?slug=${slug}`;
};

export default CorePeopleHero;
